/**
 * @file floating-video.js
 * @description Lógica do widget de vídeo flutuante nas páginas de produto
 *
 * Responsabilidade: gerenciar os três estados do widget:
 *   miniatura (idle) → modal expandido → descartado
 *
 * Estratégia de performance:
 *   - Vídeo da miniatura iniciado via requestIdleCallback (após página pronta)
 *   - Vídeo do modal criado dinamicamente no primeiro clique (sem preload)
 *   - Recursos liberados ao descartar o widget
 *
 * Camada: web (asset de produto, carregado com defer)
 */

(function () {
  'use strict';

  const widget = document.getElementById('fv-widget');
  if (!widget) return;

  const thumbnail   = document.getElementById('fv-thumbnail');
  const posterEl    = document.getElementById('fv-poster');
  const dismissBtn  = document.getElementById('fv-dismiss');
  const modal       = document.getElementById('fv-modal');
  const overlay     = document.getElementById('fv-overlay');
  const closeBtn    = document.getElementById('fv-close-modal');
  const videoContainer = document.getElementById('fv-video-container');

  const videoUrl  = widget.dataset.videoUrl;
  const posterUrl = widget.dataset.poster;

  /** @type {HTMLVideoElement|null} */
  let miniatureVideo = null;

  /** @type {HTMLVideoElement|null} */
  let modalVideo = null;

  // ── Miniatura: troca img → <video> lazy ──────────────────────────────────

  /**
   * Cria o <video> da miniatura (muted autoplay loop) e substitui o poster.
   * Chamado somente quando o browser está ocioso para não impactar LCP/FID.
   */
  function initMiniatureVideo() {
    if (!videoUrl || miniatureVideo) return;

    miniatureVideo = document.createElement('video');
    miniatureVideo.muted      = true;
    miniatureVideo.autoplay   = true;
    miniatureVideo.loop       = true;
    miniatureVideo.playsInline = true;
    miniatureVideo.preload    = 'none';
    miniatureVideo.className  = 'fv-poster';
    miniatureVideo.setAttribute('aria-hidden', 'true');
    miniatureVideo.setAttribute('tabindex', '-1');

    miniatureVideo.addEventListener('loadeddata', function () {
      if (posterEl && posterEl.parentNode) {
        posterEl.parentNode.replaceChild(miniatureVideo, posterEl);
      }
    }, { once: true });

    miniatureVideo.src = videoUrl;
    miniatureVideo.load();
  }

  // ── Modal: expandir ───────────────────────────────────────────────────────

  /**
   * Abre o modal com o player completo e pausa o vídeo da miniatura.
   */
  function openModal() {
    if (miniatureVideo) miniatureVideo.pause();

    if (!modalVideo) {
      modalVideo = document.createElement('video');
      modalVideo.controls    = true;
      modalVideo.autoplay    = true;
      modalVideo.playsInline = true;
      modalVideo.preload     = 'metadata';
      modalVideo.src         = videoUrl;
      videoContainer.appendChild(modalVideo);
    } else {
      modalVideo.play();
    }

    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(function () {
      closeBtn.focus();
    });
  }

  // ── Modal: fechar e voltar à miniatura ────────────────────────────────────

  /**
   * Fecha o modal e retoma o vídeo da miniatura no estado anterior.
   */
  function closeModal() {
    if (modalVideo) modalVideo.pause();

    modal.hidden = true;
    document.body.style.overflow = '';

    if (miniatureVideo) miniatureVideo.play();

    thumbnail.focus();
  }

  // ── Descartar widget ──────────────────────────────────────────────────────

  /**
   * Remove o widget da tela com animação de saída e libera recursos de vídeo.
   *
   * @param {Event} e - Evento de clique no botão ×
   */
  function dismissWidget(e) {
    e.stopPropagation();

    widget.style.transition = 'opacity 0.25s ease, transform 0.25s ease';
    widget.style.opacity    = '0';
    widget.style.transform  = 'translateY(12px) scale(0.9)';

    setTimeout(function () {
      widget.remove();
    }, 260);

    if (miniatureVideo) {
      miniatureVideo.pause();
      miniatureVideo.src = '';
    }
    if (modalVideo) {
      modalVideo.pause();
      modalVideo.src = '';
    }
  }

  // ── Event listeners ───────────────────────────────────────────────────────

  thumbnail.addEventListener('click', openModal);

  thumbnail.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openModal();
    }
  });

  dismissBtn.addEventListener('click', dismissWidget);

  closeBtn.addEventListener('click', closeModal);

  overlay.addEventListener('click', closeModal);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !modal.hidden) {
      closeModal();
    }
  });

  // ── Inicialização lazy ────────────────────────────────────────────────────

  if ('requestIdleCallback' in window) {
    requestIdleCallback(initMiniatureVideo, { timeout: 3000 });
  } else {
    // Safari < 16.4 não suporta requestIdleCallback
    window.addEventListener('load', function () {
      setTimeout(initMiniatureVideo, 600);
    });
  }
})();
